import {createPaginationContainer, graphql} from 'react-relay';
import {injectIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';

const PersonLoader = ({relay: {hasMore, isLoading, loadMore}, viewer, children, ...props}) => {
	const formatData = () => {
		return viewer.company.resourceBasedPersons.edges.map(person => {
			const {id, firstName, lastName, profilePictureId} = person.node;
			return {
				person: {
					id,
					firstName,
					lastName,
					profilePictureId,
				},
				resourceNumbers: {
					...person.node.resourceNumbers,
				},
			};
		});
	};
	const constructProjectToProgramMap = () => {
		const projectToProgramMap = new Map();
		viewer.company.programs?.edges.forEach(programEdge => {
			const {projects, ...rest} = programEdge.node;
			projects.edges.forEach(projectEdge => {
				const project = projectEdge.node;
				projectToProgramMap.set(btoa('ProjectType:' + project.serviceCompanyId), {...rest});
			});
		});
		return projectToProgramMap;
	};

	const loadMoreFunc = hasMore() ? loadMore : undefined;
	return children({
		data: {rows: formatData()},
		loadMore: loadMoreFunc,
		projectToProgramMap: constructProjectToProgramMap(),
		loading: isLoading(),
		...props,
	});
};

export const personLoaderQuery = graphql`
	query PersonLoaderQuery_Query(
		$searchQuery: TaskSearchQueryType
		$startYear: Int
		$startMonth: Int
		$startDay: Int
		$endYear: Int
		$endMonth: Int
		$endDay: Int
		$pageSize: Int!
		$cursor: String
	) {
		viewer {
			actualPersonId
			component(name: "utilization_period_person_loader")
			...PersonLoader_viewer
				@arguments(
					searchQuery: $searchQuery
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					pageSize: $pageSize
					cursor: $cursor
				)
		}
	}
`;

export default injectIntl(
	withRouter(
		createPaginationContainer(
			PersonLoader,
			{
				viewer: graphql`
					fragment PersonLoader_viewer on Viewer
					@argumentDefinitions(
						searchQuery: {type: "TaskSearchQueryType"}
						startYear: {type: "Int"}
						startMonth: {type: "Int"}
						startDay: {type: "Int"}
						endYear: {type: "Int"}
						endMonth: {type: "Int"}
						endDay: {type: "Int"}
						pageSize: {type: "Int!"}
						cursor: {type: "String"}
					) {
						company {
							resourceBasedPersons(
								first: $pageSize
								after: $cursor
								searchQuery: $searchQuery
								startYear: $startYear
								startMonth: $startMonth
								startDay: $startDay
								endYear: $endYear
								endMonth: $endMonth
								endDay: $endDay
							) @connection(key: "Company_resourceBasedPersons") {
								edges {
									node {
										id
										firstName
										lastName
										profilePictureId
										resourceNumbers {
											# Actuals
											actualBillableProjectMinutes
											actualNonBillableProjectMinutes
											actualInternalMinutes
											actualTotalMinutes
											actualOverMinutes
											actualDoneTaskMinutes
											actualBillableUtilization
											actualNonBillableUtilization
											actualInternalUtilization
											actualResourceUtilization

											# Plan
											plannedBillableProjectMinutes
											plannedBillableProjectMinutesWin
											plannedBillableProjectMinutesSoft
											plannedBillableProjectMinutesSoftWin
											plannedBillableProjectMinutesHard
											plannedNonBillableProjectMinutes
											plannedNonBillableProjectMinutesWin
											plannedNonBillableProjectMinutesSoft
											plannedNonBillableProjectMinutesSoftWin
											plannedNonBillableProjectMinutesHard
											plannedInternalMinutes
											plannedTotalMinutes
											plannedTotalMinutesWin
											plannedTotalMinutesSoft
											plannedTotalMinutesSoftWin
											plannedTotalMinutesHard
											plannedOverMinutes
											plannedOverMinutesWin
											plannedOverMinutesSoft
											plannedOverMinutesSoftWin
											plannedOverMinutesHard
											plannedDoneTaskMinutes
											plannedBillableUtilization
											plannedBillableUtilizationWin
											plannedBillableUtilizationSoft
											plannedBillableUtilizationSoftWin
											plannedBillableUtilizationHard
											plannedNonBillableUtilization
											plannedNonBillableUtilizationWin
											plannedNonBillableUtilizationSoft
											plannedNonBillableUtilizationSoftWin
											plannedNonBillableUtilizationHard
											plannedInternalUtilization
											plannedResourceUtilization
											plannedResourceUtilizationWin
											plannedResourceUtilizationSoft
											plannedResourceUtilizationSoftWin
											plannedResourceUtilizationHard
											plannedBillableTaskAndAllocationsCombined
											plannedNonBillableTaskAndAllocationsCombined
											plannedInternalTimeTaskAndAllocationsCombined
											plannedTotalTimeTaskAndAllocationsCombined
											plannedBillableUtilizationTaskAndAllocationsCombined
											plannedNonBillableUtilizationTaskAndAllocationsCombined
											plannedInternalUtilizationTaskAndAllocationsCombined
											plannedResourceUtilizationTaskAndAllocationsCombined
											plannedBillableTaskAndAllocationsCombinedWin
											plannedNonBillableTaskAndAllocationsCombinedWin
											plannedTotalTimeTaskAndAllocationsCombinedWin
											plannedBillableUtilizationTaskAndAllocationsCombinedWin
											plannedNonBillableUtilizationTaskAndAllocationsCombinedWin
											plannedResourceUtilizationTaskAndAllocationsCombinedWin
											plannedBillableTaskAndAllocationsCombinedSoft
											plannedNonBillableTaskAndAllocationsCombinedSoft
											plannedTotalTimeTaskAndAllocationsCombinedSoft
											plannedBillableUtilizationTaskAndAllocationsCombinedSoft
											plannedNonBillableUtilizationTaskAndAllocationsCombinedSoft
											plannedResourceUtilizationTaskAndAllocationsCombinedSoft
											plannedBillableTaskAndAllocationsCombinedSoftWin
											plannedNonBillableTaskAndAllocationsCombinedSoftWin
											plannedTotalTimeTaskAndAllocationsCombinedSoftWin
											plannedBillableUtilizationTaskAndAllocationsCombinedSoftWin
											plannedNonBillableUtilizationTaskAndAllocationsCombinedSoftWin
											plannedResourceUtilizationTaskAndAllocationsCombinedSoftWin
											plannedBillableTaskAndAllocationsCombinedHard
											plannedNonBillableTaskAndAllocationsCombinedHard
											plannedTotalTimeTaskAndAllocationsCombinedHard
											plannedBillableUtilizationTaskAndAllocationsCombinedHard
											plannedNonBillableUtilizationTaskAndAllocationsCombinedHard
											plannedResourceUtilizationTaskAndAllocationsCombinedHard

											# Actuals vs. Plan
											billableActualVsPlan
											billableActualVsPlanWin
											billableActualVsPlanSoft
											billableActualVsPlanSoftWin
											billableActualVsPlanHard
											nonBillableActualVsPlan
											nonBillableActualVsPlanWin
											nonBillableActualVsPlanSoft
											nonBillableActualVsPlanSoftWin
											nonBillableActualVsPlanHard
											internalActualVsPlan
											totalActualVsPlan
											totalActualVsPlanWin
											totalActualVsPlanSoft
											totalActualVsPlanSoftWin
											totalActualVsPlanHard

											# Remaining
											remainingBillableProjectMinutes
											remainingBillableProjectMinutesWin
											remainingBillableProjectMinutesSoft
											remainingBillableProjectMinutesSoftWin
											remainingBillableProjectMinutesHard
											remainingNonBillableProjectMinutes
											remainingNonBillableProjectMinutesWin
											remainingNonBillableProjectMinutesSoft
											remainingNonBillableProjectMinutesSoftWin
											remainingNonBillableProjectMinutesHard
											remainingInternalMinutes
											remainingTotalMinutes
											remainingTotalMinutesWin
											remainingTotalMinutesSoft
											remainingTotalMinutesSoftWin
											remainingTotalMinutesHard
											remainingOverMinutes
											remainingBillableUtilization
											remainingBillableUtilizationWin
											remainingBillableUtilizationSoft
											remainingBillableUtilizationSoftWin
											remainingBillableUtilizationHard
											remainingNonBillableUtilization
											remainingNonBillableUtilizationWin
											remainingNonBillableUtilizationSoft
											remainingNonBillableUtilizationSoftWin
											remainingNonBillableUtilizationHard
											remainingInternalUtilization
											remainingResourceUtilization
											remainingResourceUtilizationWin
											remainingResourceUtilizationSoft
											remainingResourceUtilizationSoftWin
											remainingResourceUtilizationHard
											remainingBillableTaskAndAllocationsCombined
											remainingNonBillableTaskAndAllocationsCombined
											remainingInternalTimeTaskAndAllocationsCombined
											remainingTotalTimeTaskAndAllocationsCombined
											remainingBillableUtilizationTaskAndAllocationsCombined
											remainingNonBillableUtilizationTaskAndAllocationsCombined
											remainingInternalUtilizationTaskAndAllocationsCombined
											remainingResourceUtilizationTaskAndAllocationsCombined
											remainingBillableTaskAndAllocationsCombinedWin
											remainingNonBillableTaskAndAllocationsCombinedWin
											remainingTotalTimeTaskAndAllocationsCombinedWin
											remainingBillableUtilizationTaskAndAllocationsCombinedWin
											remainingNonBillableUtilizationTaskAndAllocationsCombinedWin
											remainingResourceUtilizationTaskAndAllocationsCombinedWin
											remainingBillableTaskAndAllocationsCombinedSoft
											remainingNonBillableTaskAndAllocationsCombinedSoft
											remainingTotalTimeTaskAndAllocationsCombinedSoft
											remainingBillableUtilizationTaskAndAllocationsCombinedSoft
											remainingNonBillableUtilizationTaskAndAllocationsCombinedSoft
											remainingResourceUtilizationTaskAndAllocationsCombinedSoft
											remainingBillableTaskAndAllocationsCombinedSoftWin
											remainingNonBillableTaskAndAllocationsCombinedSoftWin
											remainingTotalTimeTaskAndAllocationsCombinedSoftWin
											remainingBillableUtilizationTaskAndAllocationsCombinedSoftWin
											remainingNonBillableUtilizationTaskAndAllocationsCombinedSoftWin
											remainingResourceUtilizationTaskAndAllocationsCombinedSoftWin
											remainingBillableTaskAndAllocationsCombinedHard
											remainingNonBillableTaskAndAllocationsCombinedHard
											remainingTotalTimeTaskAndAllocationsCombinedHard
											remainingBillableUtilizationTaskAndAllocationsCombinedHard
											remainingNonBillableUtilizationTaskAndAllocationsCombinedHard
											remainingResourceUtilizationTaskAndAllocationsCombinedHard

											# Forecast
											forecastBillableProjectMinutes
											forecastBillableProjectMinutesWin
											forecastBillableProjectMinutesSoft
											forecastBillableProjectMinutesSoftWin
											forecastBillableProjectMinutesHard
											forecastNonBillableProjectMinutes
											forecastNonBillableProjectMinutesWin
											forecastNonBillableProjectMinutesSoft
											forecastNonBillableProjectMinutesSoftWin
											forecastNonBillableProjectMinutesHard
											forecastInternalMinutes
											forecastTotalMinutes
											forecastTotalMinutesWin
											forecastTotalMinutesSoft
											forecastTotalMinutesSoftWin
											forecastTotalMinutesHard
											forecastOverMinutes
											forecastOverMinutesWin
											forecastOverMinutesSoft
											forecastOverMinutesSoftWin
											forecastOverMinutesHard
											forecastBillableUtilization
											forecastBillableUtilizationWin
											forecastBillableUtilizationSoft
											forecastBillableUtilizationSoftWin
											forecastBillableUtilizationHard
											forecastNonBillableUtilization
											forecastNonBillableUtilizationWin
											forecastNonBillableUtilizationSoft
											forecastNonBillableUtilizationSoftWin
											forecastNonBillableUtilizationHard
											forecastInternalUtilization
											forecastResourceUtilization
											forecastResourceUtilizationWin
											forecastResourceUtilizationSoft
											forecastResourceUtilizationSoftWin
											forecastResourceUtilizationHard
											forecastBillableTaskAndAllocationsCombined
											forecastNonBillableTaskAndAllocationsCombined
											forecastInternalTimeTaskAndAllocationsCombined
											forecastTotalTimeTaskAndAllocationsCombined
											forecastBillableUtilizationTaskAndAllocationsCombined
											forecastNonBillableUtilizationTaskAndAllocationsCombined
											forecastInternalUtilizationTaskAndAllocationsCombined
											forecastResourceUtilizationTaskAndAllocationsCombined
											forecastBillableTaskAndAllocationsCombinedWin
											forecastNonBillableTaskAndAllocationsCombinedWin
											forecastTotalTimeTaskAndAllocationsCombinedWin
											forecastBillableUtilizationTaskAndAllocationsCombinedWin
											forecastNonBillableUtilizationTaskAndAllocationsCombinedWin
											forecastResourceUtilizationTaskAndAllocationsCombinedWin
											forecastBillableTaskAndAllocationsCombinedSoft
											forecastNonBillableTaskAndAllocationsCombinedSoft
											forecastTotalTimeTaskAndAllocationsCombinedSoft
											forecastBillableUtilizationTaskAndAllocationsCombinedSoft
											forecastNonBillableUtilizationTaskAndAllocationsCombinedSoft
											forecastResourceUtilizationTaskAndAllocationsCombinedSoft
											forecastBillableTaskAndAllocationsCombinedSoftWin
											forecastNonBillableTaskAndAllocationsCombinedSoftWin
											forecastTotalTimeTaskAndAllocationsCombinedSoftWin
											forecastBillableUtilizationTaskAndAllocationsCombinedSoftWin
											forecastNonBillableUtilizationTaskAndAllocationsCombinedSoftWin
											forecastResourceUtilizationTaskAndAllocationsCombinedSoftWin
											forecastBillableTaskAndAllocationsCombinedHard
											forecastNonBillableTaskAndAllocationsCombinedHard
											forecastTotalTimeTaskAndAllocationsCombinedHard
											forecastBillableUtilizationTaskAndAllocationsCombinedHard
											forecastNonBillableUtilizationTaskAndAllocationsCombinedHard
											forecastResourceUtilizationTaskAndAllocationsCombinedHard

											# Tasks estimates (Planned)
											tasksEstimateBillableMinutes
											tasksEstimateNonBillableMinutes
											tasksEstimateTotalMinutes
											tasksBillableUtilization
											tasksNonBillableUtilization
											tasksResourceUtilization
											tasksOverMinutes

											# Tasks Remaining
											tasksRemainingBillableMinutes
											tasksRemainingNonBillableMinutes
											tasksRemainingTotalMinutes
											tasksRemainingOverMinutes
											tasksRemainingBillableUtilization
											tasksRemainingNonBillableUtilization
											tasksRemainingResourceUtilization
											tasksOverdueRemainingBillableMinutes
											tasksOverdueRemainingNonBillableMinutes
											tasksOverdueRemainingTotalMinutes

											# Tasks Forecast
											tasksForecastBillableMinutes
											tasksForecastNonBillableMinutes
											tasksForecastTotalMinutes
											tasksForecastOverMinutes
											tasksForecastBillableUtilization
											tasksForecastNonBillableUtilization
											tasksForecastResourceUtilization

											# Tasks Actual vs. Plan
											tasksBillableActualVsPlan
											tasksNonBillableActualVsPlan
											tasksTotalActualVsPlan

											# Planned Allocation vs. Task
											plannedBillableAllocationVsTask
											plannedBillableAllocationVsTaskWin
											plannedBillableAllocationVsTaskSoft
											plannedBillableAllocationVsTaskSoftWin
											plannedBillableAllocationVsTaskHard
											plannedNonBillableAllocationVsTask
											plannedNonBillableAllocationVsTaskWin
											plannedNonBillableAllocationVsTaskSoft
											plannedNonBillableAllocationVsTaskSoftWin
											plannedNonBillableAllocationVsTaskHard
											plannedAllocationVsTask
											plannedAllocationVsTaskWin
											plannedAllocationVsTaskSoft
											plannedAllocationVsTaskSoftWin
											plannedAllocationVsTaskHard

											# Remaining Allocation vs. Task
											remainingBillableAllocationVsTask
											remainingBillableAllocationVsTaskWin
											remainingBillableAllocationVsTaskSoft
											remainingBillableAllocationVsTaskSoftWin
											remainingBillableAllocationVsTaskHard
											remainingNonBillableAllocationVsTask
											remainingNonBillableAllocationVsTaskWin
											remainingNonBillableAllocationVsTaskSoft
											remainingNonBillableAllocationVsTaskSoftWin
											remainingNonBillableAllocationVsTaskHard
											remainingAllocationVsTask
											remainingAllocationVsTaskWin
											remainingAllocationVsTaskSoft
											remainingAllocationVsTaskSoftWin
											remainingAllocationVsTaskHard

											# Availability
											workingMinutes
											timeOffMinutes
											availableMinutes

											#Performance
											performance
										}
									}
								}
							}
							programs(first: 100000) {
								edges {
									node {
										id
										color
										prefix
										name
										projects {
											edges {
												node {
													id
													serviceCompanyId
												}
											}
										}
									}
								}
							}
						}
					}
				`,
			},
			{
				direction: 'forward',
				getConnectionFromProps({viewer}) {
					return viewer.company.resourceBasedPersons;
				},
				getFragmentVariables(prevVars) {
					return {
						...prevVars,
					};
				},
				getVariables(_, {cursor}, fragmentVariables) {
					return {
						...fragmentVariables,
						cursor,
					};
				},
				query: graphql`
					query PersonLoaderRefetchQuery(
						$pageSize: Int!
						$cursor: String
						$startYear: Int
						$startMonth: Int
						$startDay: Int
						$endYear: Int
						$endMonth: Int
						$endDay: Int
						$searchQuery: TaskSearchQueryType
					) {
						viewer {
							...PersonLoader_viewer
								@arguments(
									pageSize: $pageSize
									cursor: $cursor
									startYear: $startYear
									startMonth: $startMonth
									startDay: $startDay
									endYear: $endYear
									endMonth: $endMonth
									endDay: $endDay
									searchQuery: $searchQuery
								)
						}
					}
				`,
			}
		)
	)
);
